/* What a pattern's own OPENING says about the six option boxes.
 *
 * **A box the pattern has already decided is shown decided and locked**, the
 * owner's request on 2026-09-13, after a pattern beginning `(*UTF)(?i)` made
 * every box on the page look broken: ticking "Ignore case" changed nothing,
 * because the pattern had set it itself, and nothing on the page said so.
 *
 * **This was meant to come from PCRE2 rather than from reading the pattern**,
 * and the pinned release says no. `pcre2_pattern_info(PCRE2_INFO_ALLOPTIONS)`
 * reports start-of-pattern verbs such as `(*UCP)`, but the 10.48 documentation
 * is explicit that "option settings such as (?i) that can change within a
 * pattern do not affect the result, even if they appear right at the start"
 * - and `(?i)` is the whole of the case that mattered. So this reads the
 * prefix itself. **It is a lexer for a fixed prefix grammar, not a regex
 * parser**: the pattern structure still comes from PCRE2's own enumeration
 * (`ideas/regexray.md`), and nothing here looks past the first item that is
 * neither a `(*VERB)` nor a `(?letters)` setting.
 *
 * **Only a setting at the top level and before anything else counts.** That is
 * what makes locking honest: a leading `(?i)` applies to the whole pattern, so
 * the box can no longer change anything; `abc(?i)def` leaves the box governing
 * `abc`, so it stays live; `(?i:...)` is scoped to its group and is not a
 * setting at all. Whether the pattern later turns an option back off is
 * irrelevant, since the box could not affect that part either.
 *
 * Grammar, from pcre2pattern "Start of pattern items" and "Internal option
 * setting": any number of `(*NAME)` or `(*NAME=digits)`, then any number of
 * `(?letters)`, `(?letters-letters)` or `(?^letters)`. `(?^)` unsets i, m, n,
 * r, s and x but not U. `xx` is `x`; unsetting either cancels both. A letter
 * both before and after the hyphen is unset.
 */

/** The letters the page's boxes carry, in the page's order. */
export const LETTERS = "imsxUP";

const VERB = /\(\*([A-Z_]+)(?:=\d+)?\)/y;
const SETTING = /\(\?(\^?)([a-zA-Z]*)(?:-([a-zA-Z]*))?\)/y;

/**
 * `{ set, cleared }`: the box letters the opening turns on, and the ones it
 * turns off. A letter in neither is the box's to decide.
 */
export function leading(pattern) {
  const state = new Map();
  let at = 0;

  for (;;) {
    VERB.lastIndex = at;
    const verb = VERB.exec(pattern);

    if (verb) {
      if (verb[1] === "UCP") {
        state.set("P", true);
      }

      at = VERB.lastIndex;
      continue;
    }

    SETTING.lastIndex = at;
    const setting = SETTING.exec(pattern);

    if (!setting) {
      break;
    }

    const [, caret, on, off] = setting;

    if (caret) {
      for (const letter of "imsx") {
        state.set(letter, false);
      }
    }

    apply(state, on, true);
    apply(state, off ?? "", false);
    at = SETTING.lastIndex;
  }

  let set = "";
  let cleared = "";

  for (const letter of LETTERS) {
    if (state.get(letter) === true) set += letter;
    if (state.get(letter) === false) cleared += letter;
  }

  return { set, cleared };
}

function apply(state, letters, value) {
  for (const letter of letters) {
    if (LETTERS.includes(letter) && letter !== "P") {
      state.set(letter, value);
    }
  }
}
